import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertCircle, UserPlus, Building } from "lucide-react";
import { Logo } from "@/components/Logo";

const InviteContactLink = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState<any>(null);
  const [contactData, setContactData] = useState<any>(null);
  const [organizationData, setOrganizationData] = useState<any>(null);
  const [urlTemplate, setUrlTemplate] = useState<any>(null);
  const [error, setError] = useState<string>("");

  const token = searchParams.get("token");
  const contactId = searchParams.get("contact_id");

  useEffect(() => {
    const validateInvite = async () => {
      console.log("=== STARTING INVITATION VALIDATION ===");
      console.log("URL Parameters - Token:", token, "Contact ID:", contactId);
      
      // Ensure we're accessing this as anonymous user for invitation validation
      const { data: currentSession } = await supabase.auth.getSession();
      console.log("🔐 Current session status:", currentSession.session ? "authenticated" : "anonymous");

      if (!token) {
        console.error("❌ Missing URL token parameter");
        setError("Invalid invitation link - missing token");
        setLoading(false);
        return;
      }

      try {
        console.log("🔍 Step 1: Looking for invitation with token:", token);
        
        const { data: invitation, error: inviteError } = await supabase
          .from("user_invitations")
          .select("*")
          .eq("id", token)
          .maybeSingle();

        console.log("📧 Raw invitation query result:", { invitation, inviteError });

        if (inviteError) {
          console.error("❌ Database error fetching invitation:", inviteError);
          console.error("❌ Full error details:", {
            message: inviteError.message,
            code: inviteError.code,
            details: inviteError.details,
            hint: inviteError.hint
          });
          setError(`Database error: ${inviteError.message}. Debug info says it may be invalid or expired`);
          setLoading(false);
          return;
        }

        if (!invitation) {
          console.error("❌ No invitation found with token:", token);
          setError("Invitation not found - it may have been used or expired");
          setLoading(false);
          return;
        }

        console.log("📧 Found invitation:", invitation);

        // Check invitation status
        console.log("🔍 Step 2: Checking invitation status");
        console.log("📧 Invitation status:", invitation.status);
        
        if (invitation.status !== 'pending') {
          console.error("❌ Invitation status is not pending:", invitation.status);
          setError(`Invitation is ${invitation.status}`);
          setLoading(false);
          return;
        }

        // Check invitation expiry
        console.log("🔍 Step 3: Checking invitation expiry");
        const now = new Date();
        const expiresAt = new Date(invitation.expires_at);
        console.log("⏰ Time check - Now:", now.toISOString(), "Expires:", expiresAt.toISOString());
        
        if (expiresAt <= now) {
          console.error("❌ Invitation has expired");
          setError("Invitation has expired");
          setLoading(false);
          return;
        }

        console.log("✅ Invitation is valid and pending");

        console.log("🔍 Step 4: Looking for contact with ID:", contactId);
        
        // Get contact details (try by ID first, then by email if ID fails)
        let contact = null;
        let contactError = null;
        
        const { data: contactById, error: contactByIdError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', contactId)
          .maybeSingle();

        if (contactByIdError) {
          console.warn("⚠️ Contact ID lookup failed, trying email lookup:", contactByIdError);
          // If contact ID lookup fails, try finding by email from invitation
          const { data: contactByEmail, error: contactByEmailError } = await supabase
            .from('profiles')
            .select('*')
            .eq('email', invitation.email)
            .maybeSingle();
          
          contact = contactByEmail;
          contactError = contactByEmailError;
        } else {
          contact = contactById;
          contactError = contactByIdError;
        }

        console.log("👤 Raw contact query result:", { contact, contactError });

        if (contactError) {
          console.error("❌ Database error fetching contact:", contactError);
          setError(`Contact lookup error: ${contactError.message}`);
          setLoading(false);
          return;
        }

        if (!contact) {
          console.warn("⚠️ No contact found - this might be a new user invitation");
          console.log("🔄 Redirecting to signup page instead");
          
          // Redirect to signup page with invitation details
          navigate(`/signup?token=${token}&email=${encodeURIComponent(invitation.email)}`);
          return;
        }

        console.log("👤 Found contact:", contact);

        // Use contact's organization_id to get organization data
        const organizationId = contact.organization_id;
        console.log("🔍 Step 5: Looking for organization with ID:", organizationId);

        if (!organizationId) {
          console.error("❌ Contact has no organization_id");
          setError("Contact is not associated with an organization");
          setLoading(false);
          return;
        }

        // Get organization data using contact's organization_id
        const { data: organization, error: orgError } = await supabase
          .from("organizations")
          .select("*")
          .eq("id", organizationId)
          .maybeSingle();

        console.log("🏢 Raw organization query result:", { organization, orgError });

        if (orgError) {
          console.error("❌ Database error fetching organization:", orgError);
          setError(`Organization lookup error: ${orgError.message}`);
          setLoading(false);
          return;
        }

        if (!organization) {
          console.error("❌ No organization found with ID:", organizationId);
          setError("Organization not found");
          setLoading(false);
          return;
        }

        console.log("🏢 Found organization:", organization);

        // Load URL template for custom styling
        console.log("🔍 Step 6: Loading URL template for user_invitation");
        const { data: template, error: templateError } = await supabase
          .from("url_templates")
          .select("*")
          .eq("template_type", "user_invitation")
          .eq("is_active", true)
          .maybeSingle();

        if (templateError) {
          console.warn("⚠️ Template loading error (using fallback):", templateError);
        } else if (template) {
          console.log("🎨 Found custom template:", template);
          setUrlTemplate(template);
        } else {
          console.log("📄 No custom template found, using default styling");
        }

        console.log("✅ ALL VALIDATION PASSED - Setting state");
        setInviteData(invitation);
        setContactData(contact);
        setOrganizationData(organization);
        setLoading(false);

        console.log("🎉 Validation completed successfully");

      } catch (err) {
        console.error("💥 Unexpected error during validation:", err);
        console.error("💥 Error details:", {
          message: err instanceof Error ? err.message : 'Unknown error',
          stack: err instanceof Error ? err.stack : 'No stack trace',
          type: typeof err,
          errorObject: err
        });
        setError(`Validation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    validateInvite();
  }, [token, contactId]);

  const handleAcceptInvite = () => {
    console.log("🚀 ACCEPTING INVITATION");
    
    // Store invitation data for the signup process
    const invitationData = {
      token: token, // This is the invitation UUID
      contactId: contactId, // This is the contact UUID
      organizationId: inviteData?.organization_id, // This should be the organization UUID
      invitedRole: inviteData?.invited_role, // This should be the user_role enum
      email: inviteData?.email,
      firstName: contactData?.first_name,
      lastName: contactData?.last_name,
      phone: contactData?.phone,
      jobTitle: contactData?.job_title
    };
    
    console.log("💾 Final invitation data structure:", invitationData);
    console.log("💾 Data types:", {
      token: typeof token,
      contactId: typeof contactId,
      organizationId: typeof inviteData?.organization_id,
      invitedRole: typeof inviteData?.invited_role
    });
    
    console.log("💾 Storing invitation data in sessionStorage:", invitationData);
    
    // Store in sessionStorage to be picked up by signup process
    sessionStorage.setItem("pendingInvitation", JSON.stringify(invitationData));
    
    // Redirect to onboarding page with invitation details
    const redirectUrl = `/onboarding?token=${encodeURIComponent(token)}&contact_id=${encodeURIComponent(contactId)}&email=${encodeURIComponent(inviteData.email)}`;
    console.log("🔄 Redirecting to:", redirectUrl);
    navigate(redirectUrl);
  };

  // Process template variables
  const processTemplate = (text: string) => {
    if (!text) return text;
    
    return text
      .replace(/\{\{organizationName\}\}/g, organizationData?.name || 'Your Organization')
      .replace(/\{\{contactName\}\}/g, `${contactData?.first_name || ''} ${contactData?.last_name || ''}`.trim() || 'User')
      .replace(/\{\{invitedRole\}\}/g, inviteData?.invited_role || 'standard');
  };

  // Generate dynamic styles from template
  const getTemplateStyles = () => {
    if (!urlTemplate) return {};
    
    const styles: any = {};
    
    // Apply color configuration
    if (urlTemplate.color_config) {
      const colors = urlTemplate.color_config;
      if (colors.primary_color) {
        styles['--primary-color'] = colors.primary_color;
      }
      if (colors.background_color) {
        styles['--background-color'] = colors.background_color;
        styles.backgroundColor = colors.background_color;
      }
      if (colors.text_color) {
        styles['--text-color'] = colors.text_color;
      }
      if (colors.accent_color) {
        styles['--accent-color'] = colors.accent_color;
      }
    }
    
    // Apply font configuration
    if (urlTemplate.font_config) {
      const fonts = urlTemplate.font_config;
      if (fonts.font_family) {
        styles['--font-family'] = fonts.font_family;
        styles.fontFamily = fonts.font_family;
      }
      if (fonts.font_size) {
        styles['--font-size'] = fonts.font_size;
      }
    }
    
    return styles;
  };

  // Get template content with fallbacks
  const getTemplateContent = () => {
    const content = urlTemplate?.content_config || {};
    
    return {
      heading: processTemplate(content.heading) || "Snapshot Live",
      subheading: processTemplate(content.subheading) || "Device Monitoring & Management Platform",
      description: processTemplate(content.description) || "Join your organization and access the device portal",
      button_text: processTemplate(content.button_text) || "Accept Invitation & Create Account",
      footer: processTemplate(content.footer) || "",
      logo_url: content.logo_url || null,
      benefits_heading: processTemplate(content.benefits_heading) || "",
      benefits: content.benefits || [
        '📊 Real-time device monitoring and analytics',
        '🔧 Advanced device configuration tools', 
        '📈 Comprehensive reporting and insights',
        '👥 Collaborative team workspace',
        '🔔 Intelligent alerts and notifications'
      ]
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={getTemplateStyles()}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const templateContent = getTemplateContent();
    
    return (
      <div className="min-h-screen flex items-center justify-center p-4" 
           style={getTemplateStyles()}>
        <div className="w-full max-w-md">
          {/* Header with custom branding */}
          <div className="text-center mb-8">
            {templateContent.logo_url ? (
              <img src={templateContent.logo_url} alt="Logo" className="h-12 mx-auto mb-4" />
            ) : null}
            <div className="text-center">
              <h1 className="text-3xl font-bold mb-2" 
                  style={{ color: 'var(--text-color, #111827)', fontFamily: 'var(--font-family, inherit)' }}>
                {templateContent.heading}
              </h1>
              <p className="text-center" 
                 style={{ color: 'var(--text-color, #6b7280)' }}>
                {templateContent.subheading}
              </p>
            </div>
          </div>
          
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                Invalid Invitation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-center">{error}</p>
              <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-600">
                <p className="font-medium mb-2">Debug Info:</p>
                <div className="space-y-1">
                  <p>Token: {token}</p>
                  <p>Contact ID: {contactId}</p>
                </div>
                <p className="text-xs mt-2 text-gray-500">
                  Check the browser console (F12) for detailed logs
                </p>
              </div>
              <Button onClick={() => navigate("/")} className="w-full">
                Go to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success case - show invitation details
  const templateContent = getTemplateContent();

  return (
    <div className="min-h-screen flex items-center justify-center p-4" 
         style={getTemplateStyles()}>
      <div className="w-full max-w-md">
        {/* Header with custom branding */}
        <div className="text-center mb-8">
          {templateContent.logo_url ? (
            <img src={templateContent.logo_url} alt="Logo" className="h-12 mx-auto mb-4" />
          ) : null}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2" 
                style={{ color: 'var(--text-color, #111827)', fontFamily: 'var(--font-family, inherit)' }}>
              {templateContent.heading}
            </h1>
            <p className="text-center" 
               style={{ color: 'var(--text-color, #6b7280)' }}>
              {templateContent.subheading}
            </p>
          </div>
        </div>
        
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2 text-lg">
              <UserPlus className="h-5 w-5" />
              You're Invited!
            </CardTitle>
            <CardDescription className="text-center">
              {templateContent.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Contact Information */}
            {contactData && (
              <div 
                className="p-2 rounded-lg border"
                style={{
                  backgroundColor: `hsl(${urlTemplate?.color_config?.contact_panel_color || '217 91% 60%'} / 0.1)`,
                  borderColor: `hsl(${urlTemplate?.color_config?.contact_panel_color || '217 91% 60%'} / 0.3)`
                }}
              >
                <div className="flex items-start gap-2">
                  <CheckCircle 
                    className="h-4 w-4 mt-0.5 flex-shrink-0"
                    style={{ color: '#321521' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p 
                      className="font-semibold mb-1 text-center"
                      style={{ color: '#321521' }}
                    >
                      Contact Details
                    </p>
                    <div className="text-center">
                      <p 
                        className="text-sm"
                        style={{ color: '#321521' }}
                      >
                        <span className="font-medium">
                          {contactData.first_name} {contactData.last_name}
                        </span>
                        {' • '}
                        <span className="break-words">
                          {contactData.email}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Organization Information */}
            {organizationData && (
              <div 
                className="p-2 rounded-lg border"
                style={{
                  backgroundColor: `hsl(${urlTemplate?.color_config?.organization_panel_color || '142 76% 36%'} / 0.1)`,
                  borderColor: `hsl(${urlTemplate?.color_config?.organization_panel_color || '142 76% 36%'} / 0.3)`
                }}
              >
                <div className="flex items-start gap-2">
                  <Building 
                    className="h-4 w-4 mt-0.5 flex-shrink-0"
                    style={{ color: '#321521' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p 
                      className="font-semibold mb-1 text-center"
                      style={{ color: '#321521' }}
                    >
                      Organization Access
                    </p>
                    <p 
                      className="text-sm mb-1 text-center"
                      style={{ color: '#321521' }}
                    >
                      {organizationData.name}
                    </p>
                    <p 
                      className="text-sm text-center"
                      style={{ color: '#321521' }}
                    >
                      Role: <span className="font-medium capitalize">{inviteData?.invited_role || 'standard'}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Benefits Section */}
            {templateContent.benefits_heading && (
              <div className="space-y-3">
                <p className="text-base text-gray-700 font-medium text-center">
                  {templateContent.benefits_heading}
                </p>
              <div className="space-y-3">
                {templateContent.benefits.map((benefit, index) => {
                  // Split benefit text to extract emoji/icon and text
                  const match = benefit.match(/^([^\s]+)\s+(.+)$/);
                  const icon = match ? match[1] : '•';
                  const text = match ? match[2] : benefit;
                  
                  return (
                    <div key={index} className="flex items-center gap-3 text-base">
                      <span className="flex-shrink-0 w-6 text-center">{icon}</span>
                      <span className="text-gray-600 flex-1 text-left">{text}</span>
                    </div>
                  );
                })}
              </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3 pt-4">
              <Button 
                onClick={handleAcceptInvite} 
                className="w-full font-medium py-3"
                style={{ 
                  backgroundColor: '#DFEF97', 
                  color: 'black',
                  fontFamily: 'var(--font-family, inherit)'
                }}
              >
                {templateContent.button_text}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate("/")} 
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Decline
              </Button>
            </div>
            
            {/* Custom footer if provided */}
            {templateContent.footer && (
              <div className="mt-4 pt-4 border-t text-center text-sm" 
                   style={{ color: 'var(--text-color, #6b7280)' }}>
                {templateContent.footer}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InviteContactLink;